import React, { useEffect, useState } from 'react';
import {
  IProcessManager,
  IProcessManagerLandRegister,
} from '../../Interfaces/IProcessManager';
import { ILegalTransactionRelationship } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check,
  Close,
  ContentPasteSearch,
  CopyAll,
  Delete,
  ExpandLess,
  ExpandMore,
  WarningAmber,
} from '@mui/icons-material';
import { CustomeTextField } from '../../generic/CustomeTextField';
import { AddPMAutomation } from '../Automations/AddPMAutomation';
import { IProcessManagerAutomation } from '../../Interfaces/IProcessManagerAutomation';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { tableCellClasses } from '@mui/material/TableCell';
import { PMAutomationsTable } from '../Automations/PMAutomationsTable';
import { ILegalClient } from '../../Interfaces/ILegalClient';

interface IProps {
  processManager: IProcessManagerLandRegister;

  processManagerArray: IProcessManager[];
  setProcessManagerArray: Function;
  //
  relationsships: ILegalTransactionRelationship[];
  setRelationships: Function;
  //
  legalClients: ILegalClient[];
  legalPhases: ILegalPhaseFullObject[];
  legalReqStates: ILegalRequirementState[];
  //
  enableShowType?: boolean;
}

export const ProcessManagerLandRegisterRow: React.FC<IProps> = ({
  processManager,
  processManagerArray,
  setProcessManagerArray,
  relationsships,
  setRelationships,
  legalClients,
  legalPhases,
  legalReqStates,
  enableShowType,
}) => {
  const [currentRealEstateEntry, setCurrentRealEstateEntry] = useState(
    relationsships
      .flatMap(rel =>
        rel.RealEstateArray.flatMap(
          realestate => realestate.LandParcelEntryArray
        )
      )
      .find(
        entry =>
          entry.idRealEstateLandRegisterEntry ===
          processManager.data.idRealEstateLandRegisterEntry
      )
  );
  // Blatt-zentrische Nachverfolgung (#1108): Grundbuchinfo aus dem am Vorgang
  // verknüpften Grundbuchblatt (GrundbuchblattArray) statt dem alten
  // RealEstate-Flurstück. Neue Trackings tragen data.idGrundbuchblatt.
  const currentBlatt = relationsships
    .flatMap(rel => rel.GrundbuchblattArray ?? [])
    .find(b => b.idGrundbuchblatt === processManager.data.idGrundbuchblatt);
  const [editProcessManager, setEditProcessManager] = useState(processManager);
  const [showResult, setShowResult] = useState(false);
  //
  const [isExpantedAutomations, setIsExpantedAutomations] = useState(false);

  useEffect(() => {
    setProcessManagerArray([
      ...processManagerArray.map(x =>
        x.idProcessManager === editProcessManager.idProcessManager
          ? editProcessManager
          : x
      ),
    ]);
  }, [editProcessManager]);

  const handleRemoveMe = () => {
    setProcessManagerArray([
      ...processManagerArray.filter(
        pm => pm.idProcessManager !== processManager.idProcessManager
      ),
    ]);
  };

  const copyMe = () => {
    let tmpId = -1;
    if (processManagerArray.length > 0) {
      const tmpIdArray =
        Math.min(...processManagerArray.map(pm => pm.idProcessManager)) - 1;
      if (tmpIdArray <= tmpId) {
        tmpId = tmpIdArray - 1;
      }
    }

    if ([10, 11].includes(editProcessManager.idProcessManagerType)) {
      const newProcess: IProcessManagerLandRegister = {
        Closed_at: null,
        Created_at: new Date(),
        data: {
          idFederalStateLogin:
            editProcessManager.data.idFederalStateLogin ?? null,
          idGrundbuchblatt: editProcessManager.data.idGrundbuchblatt ?? null,
          idRealEstateLandRegisterEntry:
            editProcessManager.data.idRealEstateLandRegisterEntry ?? null,
        },
        DateToCheck: editProcessManager.DateToCheck,
        ExitCodeLastTry: null,
        idLegalClient: null,
        idLegalTransaction: -1,
        idProcessManager: tmpId,
        idProcessManagerState: 1,
        idProcessManagerType: editProcessManager.idProcessManagerType,
        idRelationship: null,
        LastTry_at: null,
        Tries: 0,
        Updated_at: new Date(),
        Automations: [
          ...editProcessManager.Automations.map(auto => ({
            ...auto,
            idProcessManager: tmpId,
          })),
        ],
      };
      setProcessManagerArray([...processManagerArray, newProcess]);
    }
  };

  if (currentRealEstateEntry === undefined && currentBlatt === undefined) {
    return (
      <TableRow>
        <TableCell colSpan={enableShowType ? 7 : 6}>
          <i>Konnte Grundbuchinformation nicht ermitteln!</i>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      {showResult && (
        <Dialog
          open
          onClose={() => setShowResult(false)}
          maxWidth='md'
          fullWidth
        >
          <DialogContent>
            <Typography variant='h5' sx={{ mb: 3 }}>
              Abgeschlossen am{' '}
              {new Date(String(editProcessManager?.Closed_at)).toLocaleString()}
            </Typography>
            {editProcessManager.idProcessManagerType === 10 && (
              <>
                <Typography variant='h6'>Aktualitätsnachweis</Typography>
                <div
                  style={{
                    pointerEvents: 'none', // deaktiviert Klicks
                  }}
                  dangerouslySetInnerHTML={{
                    __html: String(editProcessManager.data.table_html),
                  }}
                />
              </>
            )}
            <Typography variant='h6' sx={{ mt: 3 }}>
              Markentabelle
            </Typography>
            <div
              style={{
                pointerEvents: 'none', // deaktiviert Klicks
              }}
              dangerouslySetInnerHTML={{
                __html: String(editProcessManager.data.ListOfPending),
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button variant='contained' onClick={() => setShowResult(false)}>
              Schließen
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <TableRow
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderBottom: 'none',
          },
        }}
      >
        <TableCell>
          {editProcessManager.idProcessManagerState === 1 ? (
            <Close />
          ) : (
            <Check />
          )}
        </TableCell>

        <TableCell>
          {editProcessManager.Automations.length === 0 ? (
            <Stack direction='row' spacing={0.5} alignItems='center'>
              <AddPMAutomation
                processManager={editProcessManager}
                automations={editProcessManager.Automations}
                setAutomations={(
                  updatedAutomations: IProcessManagerAutomation[]
                ) =>
                  setEditProcessManager({
                    ...editProcessManager,
                    Automations: updatedAutomations,
                  })
                }
                legalClients={legalClients}
                legalPhases={legalPhases}
                legalReqStates={legalReqStates}
                setIsExpantedAutomations={setIsExpantedAutomations}
              />
              {editProcessManager.idProcessManagerState === 1 && (
                <Tooltip title='Pflicht: mindestens eine Automatisierung — sonst lässt sich der Vorgang nicht speichern.'>
                  <WarningAmber color='warning' fontSize='small' />
                </Tooltip>
              )}
            </Stack>
          ) : isExpantedAutomations ? (
            <IconButton
              size='small'
              onClick={() => setIsExpantedAutomations(false)}
            >
              <ExpandLess />
            </IconButton>
          ) : (
            <IconButton
              size='small'
              onClick={() => setIsExpantedAutomations(true)}
            >
              <ExpandMore />
            </IconButton>
          )}
        </TableCell>

        {enableShowType && (
          <TableCell>
            {editProcessManager.idProcessManagerType === 10
              ? 'Aktualitätsnachweis (und Markentabelle)'
              : editProcessManager.idProcessManagerType === 11
                ? 'Markentabelle'
                : 'FEHLER'}
          </TableCell>
        )}
        <TableCell>{editProcessManager.Tries}</TableCell>
        <TableCell>
          <CustomeTextField
            object={editProcessManager}
            attr='DateToCheck'
            label='Änderungen ab'
            setObject={setEditProcessManager}
            type='date'
            disabled={editProcessManager.idProcessManagerState !== 1}
          />
        </TableCell>
        <TableCell>
          {currentRealEstateEntry?.LandRegister ??
            currentBlatt?.Gemarkung ??
            '–'}
        </TableCell>
        <TableCell>
          {currentRealEstateEntry?.SheetLandRegister ??
            currentBlatt?.Blattnummer ??
            '–'}
        </TableCell>
        <TableCell>
          <IconButton
            size='small'
            disabled={editProcessManager.idProcessManagerState === 1}
            onClick={() => setShowResult(true)}
            title='Ergebnisse einsehen'
          >
            <ContentPasteSearch />
          </IconButton>

          {editProcessManager.idProcessManagerState === 1 ? (
            <IconButton size='small' title='Löschen' onClick={handleRemoveMe}>
              <Delete />
            </IconButton>
          ) : (
            <IconButton size='small' title='Kopieren' onClick={copyMe}>
              <CopyAll />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} style={{ padding: 0 }} />
        <TableCell colSpan={enableShowType ? 6 : 5} style={{ padding: 0 }}>
          <Collapse in={isExpantedAutomations}>
            <PMAutomationsTable
              processManager={editProcessManager}
              automations={editProcessManager.Automations}
              setAutomations={(
                updatedAutomations: IProcessManagerAutomation[]
              ) =>
                setEditProcessManager({
                  ...editProcessManager,
                  Automations: updatedAutomations,
                })
              }
              legalClients={legalClients}
              legalPhases={legalPhases}
              legalReqStates={legalReqStates}
              setIsExpantedAutomations={setIsExpantedAutomations}
            />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};
